import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';
import { ErrorState, LoadingState } from './primitives';
import { timeAgo } from '../lib/format';
import { SuperadminActionItem } from '../types/superadmin';

/**
 * This client's own slice of the cross-tenant Actions Center (ActionsCenter.tsx)
 * — same collection, same dismiss/mark-done semantics, filtered to one
 * tenantId instead of listing every client. Reads the always-current
 * SuperadminAction collection directly.
 */
export default function ClientActionsPanel({ tenantId }: { tenantId: string }) {
  const [actions, setActions] = useState<SuperadminActionItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [resolvingId, setResolvingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await api.getClientActions(tenantId);
      setActions(res.data.actions);
    } catch (err: any) {
      setLoadError(err?.message || 'Could not load actions for this client.');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleResolve = async (action: SuperadminActionItem, status: 'dismissed' | 'completed') => {
    try {
      setResolvingId(action._id);
      setActionError(null);
      await api.updateActionStatus(action._id, status);
      setActions((prev) => (prev ? prev.filter((a) => a._id !== action._id) : prev));
    } catch (err: any) {
      setActionError(err?.message || 'Could not update that action.');
    } finally {
      setResolvingId(null);
    }
  };

  return (
    <section className="section kb-product kb-client-actions">
      <div className="section-head">
        <div>
          <h3>Actions</h3>
          <p className="desc">Notifications about this client, including KB submissions and follow-up work.</p>
        </div>
      </div>

      {loading && <LoadingState label="Loading actions…" />}
      {!loading && loadError && <ErrorState message={loadError} />}
      {!loading && !loadError && actionError && <div className="panel-note neg" role="alert">{actionError}</div>}
      {!loading && !loadError && (
        <div className="flags" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 10 }}>
          {(actions || []).length === 0 && <p style={{ color: 'var(--ink-muted)', fontSize: 13.5 }}>Nothing open for this client.</p>}
          {(actions || []).map((action) => (
            <div
              key={action._id}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}
            >
              <div>
                <div style={{ fontWeight: 500 }}>{action.title}</div>
                {action.body && (
                  <div style={{ color: 'var(--ink-muted)', fontSize: 13, marginTop: 2 }}>{action.body}</div>
                )}
                <div style={{ color: 'var(--ink-disabled)', fontSize: 12, marginTop: 2 }}>{timeAgo(action.createdAt)}</div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button
                  className="btn"
                  disabled={resolvingId === action._id}
                  onClick={() => handleResolve(action, 'dismissed')}
                >
                  Dismiss
                </button>
                <button
                  className="btn"
                  disabled={resolvingId === action._id}
                  onClick={() => handleResolve(action, 'completed')}
                >
                  Mark done
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
